// State machine driven by SSE contentType markers (AssistantHandover, MessageChunk, etc.)
// idle -> optimistic_thinking -> agent_thinking -> responding -> idle

import type { InternalMessage } from '@/utils/ai-adapters/xdc/types'
import type { AssistantMessageConfig } from '@/ui/components/ChatMessageTypes/Assistant/types/AssistantMessageTypes'
import { getAgentLoadingStrategy } from './agentLoadingStrategy'

export type AgentLoadingState =
    | 'idle'                 // No request in progress, no SSE stream
    | 'optimistic_thinking'  // API called, no stream tokens yet
    | 'agent_thinking'       // First solution detected, more incoming
    | 'responding'           // Group formed, all solutions available

export interface AgentLoadingStateResult {
    state: AgentLoadingState
    groupSize: number        // number of solutions in the last group (0 when not responding)
}

// Count trailing consecutive AssistantHandover messages belonging to the same request
const countLastHandoverGroup = (msgs: InternalMessage[]): number => {
    let count = 0
    for (let i = msgs.length - 1; i >= 0; i--) {
        const msg = msgs[i]
        if (msg?.role !== 'assistant' || !msg?.assistantHandover) break
        count++
        if (msg.userMessageId) break
    }
    return count
}

// Determine the current agent loading state based on the last message and whether we're waiting for a response
export const useAgentLoadingState = (
    activeAssistantConfig: AssistantMessageConfig | undefined,
    messageList: InternalMessage[],
    waitingForResponse: boolean
): AgentLoadingStateResult => {
    const strategy = getAgentLoadingStrategy(activeAssistantConfig?.endpoint)

    if (strategy !== 'dynamic_state_machine') {
        return { state: 'idle', groupSize: 0 }
    }

    const lastMsg = messageList[messageList.length - 1]
    const lastMsgIsAssistant = lastMsg?.role === 'assistant'

    if (lastMsgIsAssistant) {
        const groupSize = countLastHandoverGroup(messageList)
        if (groupSize >= 1) {
            return { state: 'responding', groupSize }
        }
    }

    if (!waitingForResponse) {
        return { state: 'idle', groupSize: 0 }
    }

    // Only look at messages after the last user message so previous requests'
    // assistantHandover entries don't prematurely advance the state to agent_thinking.
    const lastUserIndex = messageList.reduce(
        (acc, m, i) => (m.role === 'user' ? i : acc),
        -1
    )
    const hasHandover = messageList
        .slice(lastUserIndex + 1)
        .some(m => m.role === 'assistant' && m.assistantHandover)

    return {
        state: hasHandover ? 'agent_thinking' : 'optimistic_thinking',
        groupSize: 0,
    }
}
